import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as locationActions } from '../../actions/locationActions';
import * as types from '../../constants/actionTypes';
import DrawerV6 from '@Common/Drawer/DrawerV6/DrawerV6';
import LocationForm from './LocationForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { DATE_FORMAT } from '../../constants/constants';
import { isValidEmailAddress } from '../../helpers/validation';
import Moment from 'moment';

export class LocationDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        const newLocation = {
            id: 0, // Only display if editing and user is EH Admin
            name: '',
            abbreviation: '',
            generalManagerName: '',
            generalManagerEmail: '',
            billingInfo: '',
            address: '',
            city: '',
            stateProvince: '',
            division: '',
            unit: '',
            generation: '',
            openingDate: '',
            brandId: "",
            managementCompanyId: "",
            areaId: "",
            regionId: "",
            projectManagerIds: [],
            locationTagIds: [],
            continentId: "",
            countryId: "",
            locationTypeId: "",
            isActive: true,
            createdByName: '', // Only display if Editing
            createdDate: '',  // Only display if Editing
            modifiedByName: '',  // Only display if Editing
            modifiedDate: ''  // Only display if Editing
        };

        this.state = {
            location: newLocation,
            trackingUpdates: [],
            isFormValid: true,
            isGeneralManagerEmailValid: true,
            areas: [],
            brands: [],
            continents: [],
            countries: [],
            locationTypes: [],
            managementCompanies: [],
            regions: [],
            projectManagers: [],
            tags: [],
            isLoading: false,
            isSaving: false
        };
    }

    componentDidMount() {
        this.loadForm();
    }

    onFormChange = (name, value) => {
        this.setState({ location: { ...this.state.location, [name]: value } });
    }

    onCancel = () => {
        this.setState({ isFormValid: true });
        this.props.onCancel();
    }

    loadForm = async () => {
        try {
            this.setState({ isLoading: true });
            await Promise.all([this.loadFilters(), this.loadLocation()])
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadFilters = async () => {
        const response = await this.props.locationActions.loadFormFilters(this.props.subscriptionId);
        if (response.type === types.LOAD_LOCATION_FORM_FILTERS_SUCCESS) {
            this.setState({
                areas: response.payload.areas,
                brands: response.payload.brands,
                continents: response.payload.continents,
                countries: response.payload.countries,
                locationTypes: response.payload.locationTypes,
                managementCompanies: response.payload.managementCompanies,
                regions: response.payload.regions,
                projectManagers: response.payload.projectManagers,
                tags: response.payload.tags
            });
        } else if (response.type === types.LOAD_LOCATION_FORM_FILTERS_FAILURE) {
            Notification("error", "An Error Occurred", response.payload.message);
        }
    }

    loadLocation = async () => {
        if (this.props.locationId) {
            const response = await this.props.locationActions.loadLocation(this.props.locationId, this.props.subscriptionId);
            if (response.type === types.LOAD_LOCATION_SUCCESS) {
                let location = response.payload.location;
                location.projectManagerIds = (response.payload.projectManagerIds) ? response.payload.projectManagerIds : "";
                location.locationTagIds = (response.payload.locationTagIds) ? response.payload.locationTagIds : "";
                
                // Format the opening date for use in DatePicker
                const openingDate = new Date(location.openingDate);
                location.openingDate = Moment(openingDate).format(DATE_FORMAT);

                // Format audit dates just to match the rest of the date formatting
                const dateCreated = new Date(location.createdDate);
                location.dateCreated = Moment.utc(dateCreated).format(DATE_FORMAT);
                const dateModified = new Date(location.modifiedDate);
                location.dateModified = Moment.utc(dateModified).format(DATE_FORMAT);

                // Add audit names directly to Location object
                location.createdByName = response.payload.createdByName;
                location.modifiedByName = response.payload.modifiedByName;

                this.setState({ location: location, trackingUpdates: response.payload.trackingUpdates });
            } else if (response.type === types.LOAD_LOCATION_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        }
    }

    validateForm = () => {
        const {name, abbreviation, openingDate, areaId, brandId, continentId, countryId, locationTypeId, managementCompanyId, regionId, generalManagerEmail } = this.state.location;

        // Email can either have no value or match the standard RegEx from Mozilla
        const emailValid = (!generalManagerEmail || generalManagerEmail.trim().length === 0) || isValidEmailAddress(generalManagerEmail);

        if (
            name && name.trim().length > 0 &&
            abbreviation && abbreviation.trim().length > 0 &&
            openingDate && openingDate.trim().length > 0 &&
            areaId && 
            brandId &&
            continentId &&
            countryId &&
            locationTypeId &&
            managementCompanyId &&
            regionId &&
            emailValid
        ) {
            this.setState({ isFormValid: true, isGeneralManagerEmailValid: emailValid, isLoading: true });
            return true;
        } else {
            this.setState({ isFormValid: false, isGeneralManagerEmailValid: emailValid, isLoading: false });
            return false;
        }
    }

    onSave = async () => {
        try {
            if (this.validateForm()) {
                const request = {
                    ...this.state.location,
                    subscriptionId: this.props.subscriptionId
                }

                const result = await this.props.locationActions.saveLocation(request);
                if (result.type === types.CREATE_LOCATION_SUCCESS || result.type === types.UPDATE_LOCATION_SUCCESS) {
                    Notification("success", "Success", "Location saved successfully");
                    return true;
                } else if (result.type === types.CREATE_LOCATION_FAILURE || result.type === types.UPDATE_LOCATION_FAILURE) {
                    Notification("error", "Error", result.payload.message);
                    return false;
                }
            } else {
                return false;
            }
        } catch (error) {
            Notification("error", "Error", error);
            return false;
        } finally {
            this.setState({ isSaving: false });
        }
    }

    onSaveAndClose = async () => {
        this.setState({ isSaving: true });
        const isSuccess = await this.onSave();
        if (isSuccess) {
            this.setState({ isFormValid: true, drawerOpen: false });
            this.props.onSave();
        } else {
            this.setState({ isLoading: false });
        }
    }

    render() {
        const title = (this.state.location.id === 0) ? 'Create New Location or Team' : 'Edit Location or Team'

        return (
            <DrawerV6 isWide={true} title={title}
                onSaveClick={this.onSaveAndClose}
                saveText="Save and Close"
                onCancelClick={this.onCancel}
                menuButtons={this.props.menuButtons}
                currentDrawerKey={this.props.currentDrawerKey}
                isSaveDisabled={this.state.isSaving}>

                <LoadingIndicator isLoading={this.state.isLoading}>
                    <LocationForm
                        isFormValid={this.state.isFormValid}
                        location={this.state.location}
                        isUserEhAdmin={this.props.isUserEhAdmin}
                        areas={this.state.areas}
                        brands={this.state.brands}
                        continents={this.state.continents}
                        countries={this.state.countries}
                        locationTypes={this.state.locationTypes}
                        managementCompanies={this.state.managementCompanies}
                        regions={this.state.regions}
                        projectManagers={this.state.projectManagers}
                        onFormChange={this.onFormChange}
                        isGeneralManagerEmailValid={this.state.isGeneralManagerEmailValid}
                        trackingUpdates={this.state.trackingUpdates}
                        tags={this.state.tags}
                    />
                </LoadingIndicator>
            </DrawerV6>
        )
    }
}

LocationDrawer.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    locationId: PropTypes.number,
    onSave: PropTypes.func.isRequired,
    onSaveAndSelectBaselines: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isUserEhAdmin: PropTypes.bool.isRequired,
    menuButtons: PropTypes.array.isRequired,
    currentDrawerKey: PropTypes.string.isRequired,
    locationActions: PropTypes.object.isRequired
};

function mapStateToProps(state) {
    return {
        isUserEhAdmin: state.user.ehAdminRole ? true : false
    }
}

function mapDispatchToProps(dispatch) {
    return {
        locationActions: bindActionCreators(locationActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationDrawer);
